import 'components/shortlist/actions/save/index.css'
import './index.css'

import * as React from 'react'
import { connect } from 'react-redux'

import {
  listFormClose,
  listFormModifyLists,
  listFormSave,
  loadEmailGroups,
} from '@disco/state-management/actions/EmailGroupActions'
import type { CheckboxState } from '@disco/state-management/reducers/forms/list'
import {
  getContacts,
  getFormContacts,
  getSelectAllContactIds,
} from '@disco/state-management/selectors/contacts'

import type { Contact, ContactId } from 'types/list'
import type { State } from 'types/redux'

import DocumentHeadTags from 'components/document-head-tags'
import { Box } from 'components/ds'
import { DiscoModal } from 'components/ui'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Checkbox from 'components/ui/checkbox'
import ChoiceList from 'components/ui/choice-list'
import Heading from 'components/ui/heading'
import Input from 'components/ui/input'
import Label from 'components/ui/label'
import modalStyles from 'components/utils/modalStyles'

function mapStateToProps(state: State) {
  return {
    emailGroups: state.emailGroups,
    listForm: state.forms.listForm,
    contacts: getFormContacts(state),
    selectedAll: getContacts(state).selectedAll,
    // when select-all is active this is the effective selection (minus
    // individually deselected contacts), not the full roster
    ids: getSelectAllContactIds(state),
  }
}

const mapDispatchToProps = {
  listFormModifyLists,
  listFormSave,
  listFormClose,
  loadEmailGroups,
}
type Props = {
  emailGroups: State['emailGroups']
  listForm: State['forms']['listForm']
  contacts: Array<Contact>
  listFormModifyLists: typeof listFormModifyLists
  listFormSave: typeof listFormSave
  listFormClose: typeof listFormClose
  loadEmailGroups: typeof loadEmailGroups
  selectedAll: boolean
  ids: ContactId[]
}
type OwnState = {
  consent: boolean
  initialGroupsState: Record<string, CheckboxState>
  newGroupName: string
}

class ContactListChange extends React.Component<Props, OwnState> {
  componentDidMount() {
    this.props.loadEmailGroups()
  }

  state: OwnState = {
    consent: false,
    initialGroupsState: this.props.emailGroups.emailGroups.reduce(
      (acc: Record<string, CheckboxState>, item) => {
        return {
          ...acc,
          [item.id]: this._isListItemSelected(
            item,
            this.props.selectedAll
              ? this.props.ids
              : this.props.contacts.map(c => c.id),
          ),
        }
      },
      {},
    ),
    newGroupName: '',
  }

  _isListItemSelected(list: any, contactIds: ContactId[]): CheckboxState {
    if (!list.recipients) return 'none'
    const changed = this.props.listForm.listsToChange[list.id]

    if (changed) {
      return changed
    }

    const match = list.recipients.filter(
      r => r.contact && contactIds.includes(r.contact.id),
    )

    if (match.length === 0) {
      return 'none'
    } else if (match.length === contactIds.length) {
      return 'all'
    } else {
      return 'some-unchanged'
    }
  }

  nextState(state: CheckboxState): CheckboxState | null | undefined {
    switch (state) {
      case 'none':
        return 'all'

      case 'all':
        return 'none'

      case 'some-unchanged':
        return 'some-all'

      case 'some-all':
        return 'some-none'

      case 'some-none':
        return 'some-unchanged'
    }
  }

  _clickListItem = (listId: number, currentVal: CheckboxState) => {
    let next = this.nextState(currentVal)
    if (!next) return
    this.props.listFormModifyLists(listId, next)
  }
  _onClose = () => {
    this.props.listFormClose()
  }
  _handleSubmit = () => {
    let groupName = this.state.newGroupName.trim()
    if (!groupName) groupName = null
    this.props.listFormSave(groupName)
  }

  _renderGroups() {
    const {
      emailGroups,
      contacts: selectedContacts,
      selectedAll,
      ids,
    } = this.props

    if (!emailGroups.isFetched) {
      return null
    }

    const lists = emailGroups.emailGroups.map(item => {
      const selected = this._isListItemSelected(
        item,
        selectedAll ? ids : selectedContacts.map(c => c.id),
      )

      return {
        label: item.name,
        value: item.id,
        selected: selected !== 'none' && selected !== 'some-none',
        partial: selected === 'some-unchanged',
        // TODO: refactor and move event handler to ChoiceList prop
        onChange: () => this._clickListItem(item.id, selected),
      }
    })
    return <ChoiceList title="Mailing Lists" items={lists} />
  }

  _handleConsent = (label, value) => {
    this.setState({
      consent: value,
    })
  }
  _areMailingListsAdded = (listsToChange, initialState) => {
    const result = Object.keys(listsToChange).reduce((acc, k) => {
      const changedItem = listsToChange[parseInt(k)]
      return (
        acc ||
        (changedItem !== initialState[k] &&
          (changedItem === 'all' || changedItem === 'some-all'))
      )
    }, false)
    return result
  }
  _onNewGroupNameChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    this.setState({
      newGroupName: e.target.value,
    })

  render() {
    const saving = this.props.listForm.saving
    const title = 'Manage mailing lists'
    const selected = this.props.selectedAll
      ? this.props.ids.length
      : this.props.contacts.length

    const groups = this._renderGroups()

    const requireConsent =
      this.state.newGroupName.trim().length > 0 ||
      this._areMailingListsAdded(
        this.props.listForm.listsToChange,
        this.state.initialGroupsState,
      )

    return (
      <div>
        <DocumentHeadTags title={`${title} | DISCO`} />
        <DiscoModal onRequestClose={this._onClose} isOpen style={modalStyles}>
          <div className="Form Form_Channel">
            <Heading size="600">{title}</Heading>
            <div className="form-note form-note--spaced">
              {selected} <span>{selected !== 1 ? 'contacts' : 'contact'}</span>{' '}
              <span>selected</span>
            </div>
            {groups && <div className="ContactListChange_Groups">{groups}</div>}
            <Box mb="4">
              <Label
                caption={`Create a new list with selected ${
                  selected !== 1 ? 'contacts' : 'contact'
                }`}
              />
              <Input
                className="Form_Input"
                value={this.state.newGroupName}
                onChange={this._onNewGroupNameChange}
              />
            </Box>
            {this.props.listForm.error && (
              <div className="Login_ValidationMessage">
                {this.props.listForm.error.toString()}
              </div>
            )}
            <Box mb="4">
              <Checkbox
                label="The people I'm adding to my mailing lists have given me permission to email them."
                checked={this.state.consent}
                onChange={this._handleConsent}
                disabled={!requireConsent}
              />
            </Box>
            <div className="Form_ButtonsContainer separated-small">
              <ButtonContainer>
                <Button caption="Cancel" onClick={this._onClose} />
                <Button
                  appearance="primary"
                  caption="Save changes"
                  disabled={!this.state.consent && requireConsent}
                  progressIndicator={saving}
                  progressCaption="Saving changes"
                  onClick={this._handleSubmit}
                />
              </ButtonContainer>
            </div>
          </div>
        </DiscoModal>
      </div>
    )
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ContactListChange) as any
